/**
 * 10/07/2026 (P2 dette technique) — Cycle des congés extrait de
 * coreSlice.nextDay (bloc inline ~160 lignes → helper orchestré).
 *
 * Périmètre (comportement inchangé) :
 *   1. 1er janvier : départs "pression congés" (cap annuel atteint),
 *      recharge des soldes, reset des compteurs annuels + flags Nadia.
 *   2. Retours de congés (+ libération de l'intérim auto).
 *   3. Proposition de plan de congés par Nadia (1×/an, mois ≥ 5).
 *   4. Auto-déclenchement d'un bloc de congés (jours ouvrés, hors 1er janv).
 *   5. Pénalité de pression hebdomadaire + alertes mi-cap Nadia.
 *   6. Rapport trimestriel des congés (90 j, idempotent).
 *
 * NOTE : mutation volontaire de `state.vehicles` (libération intérim) —
 * même sémantique que le bloc inline d'origine, le state est relu plus
 * bas dans nextDay.
 */
import { generateId } from '../helpers';
import {
  buildHolidayPlan,
  shouldNadiaProposeHolidayPlan,
  buildNadiaPlanProposalMessage,
} from './holidayPlanning';
import {
  applyYearStartHolidayReset,
  applyMonthlyHolidayAccrual,
  applyHolidayReturns,
  triggerAutoHoliday,
  buildHolidayStartNotification,
  buildHolidayReturnNotification,
  applyHolidayPressurePenalty,
  buildHolidayPressureNotification,
  resetYearlyPressureCounters,
  applyPressureDepartureNotices,
  buildPressureDepartureLetter,
  buildPressureNoticeNotification,
  pickNadiaPressureAlerts,
  buildNadiaPressureAlertMessage,
  resetNadiaPressureAlertFlags,
} from './driverHolidayTick';
import {
  shouldEmitHolidayQuarterlyReport,
  buildHolidayQuarterlyReport,
} from './hrHolidayReport';

export function runHolidayCycleTick({ state, working, drivers, newNotifications, newAlerts, get, set }) {
  let driversAfterHoliday = drivers;
  // Le calendrier global (newDay/newMonth/newYear) est posé plus tard dans
  // nextDay (étape 6) : on projette l'incrément localement pour détecter
  // le 1er janvier dès maintenant.
  const _nextDayTmp = state.day + 1;
  const _wrapsMonth = _nextDayTmp > 30;
  const _projectedMonth = _wrapsMonth ? ((state.month % 12) + 1) : state.month;
  const _projectedDay = _wrapsMonth ? 1 : _nextDayTmp;
  const _projectedYear = _wrapsMonth && state.month === 12 ? state.year + 1 : state.year;
  const _tickTotalDays = (state.totalDays || 0) + 1;
  const isYearStart = (_projectedDay === 1 && _projectedMonth === 1 && _projectedYear > 1);

  // 23/07/2026 (choix user 3a) — plus de départ SEC au 1er janvier.
  // Le 1er DÉCEMBRE, chaque chauffeur au cap de pression congés dépose un
  // préavis de 30 j (bannière RH) + lettre de décision (accorder 5 j de
  // congés / acter le départ). Sans réponse → départ au 1er janvier via
  // driverDepartureTick (indemnité débitée à ce moment-là).
  const isDec1 = (_projectedDay === 1 && _projectedMonth === 12);
  if (isDec1) {
    const noticeOut = applyPressureDepartureNotices(
      driversAfterHoliday, _tickTotalDays, _projectedYear,
    );
    driversAfterHoliday = noticeOut.drivers;
    const pushMsg = get().pushCustomMessage;
    noticeOut.notices.forEach(notice => {
      newNotifications.push({ id: generateId(), ...buildPressureNoticeNotification(notice, _tickTotalDays) });
      newAlerts.push({
        id: generateId(),
        type: 'driver_pressure_notice',
        message: `${notice.name} dépose un préavis (congés non accordés) — réagissez avant le 1er janvier.`,
        day: _tickTotalDays,
      });
      if (typeof pushMsg === 'function') {
        try { pushMsg(buildPressureDepartureLetter(notice)); }
        catch (err) { console.warn('[holidayCycleTick] pressure letter push failed:', err); }
      }
    });
  }
  driversAfterHoliday = applyYearStartHolidayReset(driversAfterHoliday, isYearStart, _projectedYear);
  // 17/07/2026 — cumul mensuel des congés (+2,5 j au 1er de chaque mois,
  // plafond 30 j, idempotent par tag "année-mois").
  driversAfterHoliday = applyMonthlyHolidayAccrual(
    driversAfterHoliday,
    _projectedDay === 1,
    `${_projectedYear}-${_projectedMonth}`,
  );
  driversAfterHoliday = resetYearlyPressureCounters(driversAfterHoliday, isYearStart);
  driversAfterHoliday = resetNadiaPressureAlertFlags(driversAfterHoliday, isYearStart);

  // Retours de congés (détectés AVANT application pour les notifications).
  const returningNames = driversAfterHoliday
    .filter(d => d.onHoliday && d.holidayEndDay != null && _tickTotalDays >= d.holidayEndDay)
    .map(d => d.name);
  const _holidayReturnOut = applyHolidayReturns(driversAfterHoliday, _tickTotalDays, state.vehicles || []);
  driversAfterHoliday = _holidayReturnOut.drivers;
  if (_holidayReturnOut.interimVehicleIdsToRelease.length > 0) {
    const releasedSet = new Set(_holidayReturnOut.interimVehicleIdsToRelease);
    state.vehicles = (state.vehicles || []).map(v =>
      releasedSet.has(v.id) ? { ...v, useInterim: false, interimAutoActivated: false } : v
    );
  }
  returningNames.forEach(name => {
    newNotifications.push({ id: generateId(), ...buildHolidayReturnNotification(name, _tickTotalDays) });
  });

  // Nadia propose un plan de congés étalé (1×/an, mois ≥ 5, risque haut).
  try {
    if (shouldNadiaProposeHolidayPlan(state)) {
      const _plan = buildHolidayPlan(driversAfterHoliday, _tickTotalDays);
      if (_plan.length > 0 && typeof get().pushCustomMessage === 'function') {
        const _payload = buildNadiaPlanProposalMessage(_plan, state.year || 1);
        get().pushCustomMessage(_payload);
        set({ holidayPlanProposedYear: state.year || 1 });
      }
    }
  } catch (err) {
    console.warn('[holidayCycleTick] Nadia holiday plan proposal failed:', err);
  }

  // Auto-déclenchement : jours ouvrés uniquement, jamais le 1er janvier.
  let holidaysGrantedThisTick = 0;
  let holidayDriversStartedThisTick = 0;
  if (working && !isYearStart) {
    const triggerOut = triggerAutoHoliday(driversAfterHoliday, _tickTotalDays, state.vehicles || []);
    driversAfterHoliday = triggerOut.drivers;
    if (triggerOut.startedDriverId) {
      const justLeft = driversAfterHoliday.find(d => d.id === triggerOut.startedDriverId);
      newNotifications.push({
        id: generateId(),
        ...buildHolidayStartNotification(triggerOut.startedDriverName, _tickTotalDays, justLeft?.holidayDaysRemaining || 0),
      });
      holidaysGrantedThisTick = 5; // HOLIDAY_BLOCK_LENGTH
      holidayDriversStartedThisTick = 1;
    }
  }

  // Pénalité de pression hebdomadaire (notification au premier hit de l'année).
  const pressureOut = applyHolidayPressurePenalty(driversAfterHoliday, _tickTotalDays, _projectedMonth);
  driversAfterHoliday = pressureOut.drivers;
  pressureOut.penalizedDriverIds.forEach(did => {
    const d = driversAfterHoliday.find(x => x.id === did);
    if (d && d.holidayPressureLossThisYear === 1) {
      newNotifications.push({
        id: generateId(),
        ...buildHolidayPressureNotification(d.name, d.holidayDaysRemaining || 0, _tickTotalDays),
      });
    }
  });

  // Alerte mi-cap personnalisée de Nadia (1×/chauffeur/an, si en poste).
  const hasNadia = !!(state.hiredSupport || {})['role-nadia-cheikh'];
  if (hasNadia) {
    const nadiaOut = pickNadiaPressureAlerts(driversAfterHoliday);
    driversAfterHoliday = nadiaOut.drivers;
    const pushMsg = get().pushCustomMessage;
    if (typeof pushMsg === 'function') {
      nadiaOut.alerts.forEach(alert => {
        try {
          pushMsg(buildNadiaPressureAlertMessage(alert.name, alert.balance, alert.lossSoFar));
        } catch (err) { console.warn('[nadia-alert] push failed:', err); }
      });
    }
  }

  // Rapport trimestriel des congés (tous les 90 j, idempotent).
  let lastHolidayQuarterlyReportDay = state.lastHolidayQuarterlyReportDay || 0;
  if (shouldEmitHolidayQuarterlyReport({ totalDays: _tickTotalDays, lastHolidayQuarterlyReportDay })) {
    const pushMsg = get().pushCustomMessage;
    if (typeof pushMsg === 'function') {
      try {
        pushMsg(buildHolidayQuarterlyReport(driversAfterHoliday, _tickTotalDays, hasNadia));
      } catch (err) { console.warn('[holiday-quarterly] push failed:', err); }
    }
    lastHolidayQuarterlyReportDay = _tickTotalDays;
  }

  return {
    drivers: driversAfterHoliday,
    lastHolidayQuarterlyReportDay,
    holidaysGrantedThisTick,
    holidayDriversStartedThisTick,
  };
}
